import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  DashboardCompaniesFiltersFormProps,
  DashboardCompaniesFiltersFormSchema,
} from './DashboardCompaniesFiltersForm.types';
import { getCompaniesFilterSchema } from './DashboardCompaniesFiltersForm.schema';
import { dashboardCompaniesFiltersFormData } from './DashboardCompaniesFiltersForm.data';
import Select from '../../../../../../commons/Select/Select';
import { ButtonColor, ButtonSize } from '../../../../../../designSystem/Button/Button.types';
import Button from '../../../../../../designSystem/Button/Button';
import { DashboardCompaniesContext } from '../../DashboardCompanies.context';
import { useNafList } from '../../../../../../../hooks/fetchers/useNafList/useNafList';
import departments from '../../../../../../../assets/json/departements-region.json';
import { SelectOption } from '../../../../../../commons/Select/Select.types';
import RSelect from 'react-select/dist/declarations/src/Select';

export default ({ closeModal }: DashboardCompaniesFiltersFormProps) => {
  const { setFilters } = useContext(DashboardCompaniesContext);

  const fieldsOfActivityRef = useRef<RSelect[]>([]);

  useEffect(() => {
    fieldsOfActivityRef.current = fieldsOfActivityRef.current.slice(0, [...Array(4)].length);
  }, []);

  const { data: NAFList } = useNafList();

  const [inputValue, setInputValue] = useState<string>()

  const formProps = useForm<DashboardCompaniesFiltersFormSchema>({
    resolver: yupResolver(getCompaniesFilterSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
  } = formProps;

  // const domains = useWatch({
  //   control,
  //   name: 'domains',
  // });

  const fieldsOfActivity = useWatch({
    control,
    name: 'fieldsOfActivity',
  });

  const region = useWatch({
    control,
    name: 'region',
  });

  const department = useWatch({
    control,
    name: 'department',
  });

  // useEffect(() => {
  //   if (domains?.length === 0) {
  //     setValue('fieldsOfActivity', [], { shouldValidate: true });
  //   }
  // }, [domains]);

  const isResetable = useMemo(
    () => Boolean(fieldsOfActivity || region || department),
    [fieldsOfActivity, region, department]
  );

  const onReinitialize = () => {
    [...Array(4)].map((_, index) => {
      fieldsOfActivityRef.current[index]?.clearValue();
    });
    setFilters({
      domains: [],
      fieldsOfActivity: [],
      region: undefined,
      department: undefined,
    });
    // closeModal()
  };

  const onSubmit = handleSubmit(({ fieldsOfActivity, region, department }) => {
    setFilters((state) => ({
      ...state,
      fieldsOfActivity: (fieldsOfActivity as string[]) || [],
      region,
      department,
    }));
    closeModal();
  });

  const updatedFormData = useMemo(
    () =>
      dashboardCompaniesFiltersFormData.map((row) =>
        row.map((item) => {
          switch (item.name) {
            case 'fieldsOfActivity':
              return {
                ...item,
                // disabled: !domains || domains.length === 0,
                options: NAFList
                  ?.filter((item) => item.description.toLowerCase().includes(inputValue?.toLowerCase() || 'Not in list'))
                  ?.map(({ NAF_code, description }) => ({
                    label: description,
                    value: NAF_code.toString(),
                  })),
              };
            case 'region':
              return {
                ...item,
                options: department
                  ? departments
                      .reduce(
                        (acc, item) => {
                          const isRegionExists = acc.find(
                            (accItem) => accItem.region_name === item.region_name
                          );
                          if (!isRegionExists) {
                            return [...acc, item];
                          } else {
                            return acc;
                          }
                        },
                        [] as typeof departments
                      )
                      .filter((item) => item.dep_name === department)
                      .map((dep) => ({
                        value: dep.region_name,
                        label: dep.region_name,
                      }))
                  : departments
                      .reduce(
                        (acc, item) => {
                          const isRegionExists = acc.find(
                            (accItem) => accItem.region_name === item.region_name
                          );
                          if (!isRegionExists) {
                            return [...acc, item];
                          } else {
                            return acc;
                          }
                        },
                        [] as typeof departments
                      )
                      .map((dep) => ({
                        value: dep.region_name,
                        label: dep.region_name,
                      })),
              };
            case 'department':
              return {
                ...item,
                options: region
                  ? departments
                      .filter((item) => item.region_name === region)
                      .map((dep) => ({
                        value: dep.dep_name,
                        label: dep.dep_name,
                      }))
                  : departments.map((dep) => ({
                      value: dep.dep_name,
                      label: dep.dep_name,
                    })),
              };
            default:
              return item;
          }
        })
      ),
    [NAFList, inputValue, region, department]
  );

  return (
    <>
      <form className="form" style={{ width: 'calc(100% - var(--spacing-mToL) * 2)' }}>
        {updatedFormData.map((row, rowIndex) => (
          <div key={`row#${rowIndex}`} className="form__row">
            {row.map(({ name, placeholder, label, options, ...props }) => (
              <Controller
                key={`controlledInput#${rowIndex}`}
                name={name}
                render={({ field: { onChange } }) => (
                  <Select
                    // @ts-ignore
                    ref={(ref) => (fieldsOfActivityRef.current[rowIndex] = ref)}
                    key={`select-${name}`}
                    placeholder={placeholder || ''}
                    error={errors[name]?.message as string}
                    onInputChange={(value: string) => setInputValue(value)}
                    {...{ label, options }}
                    {...props}
                    // @ts-ignore
                    onChange={(arr: SelectOption | SelectOption[]) => {
                      if (['department', 'region'].includes(name)) {
                        onChange((arr as SelectOption)?.value);
                      } else {
                        const values = (arr as SelectOption[]).map((item) => {
                          if (typeof item === 'string') {
                            return item;
                          } else {
                            return item.value;
                          }
                        });
                        onChange(values);
                      }
                    }}
                    // @ts-ignore
                    noOptionsMessage={({ inputValue }) => !inputValue ? 'Chercher un domaine' : 'Aucun résultat trouvé'}
                    isMulti={!['department', 'region'].includes(name)}
                  />
                )}
                {...{ control }}
              />
            ))}
          </div>
        ))}
        {isResetable && (
          <Button
            style={{ alignSelf: 'unset' }}
            onClick={(e) => {
              e.preventDefault();
              onReinitialize();
            }}
            size={ButtonSize.Large}
            color={ButtonColor.Contrast}>
            Réinitialiser
          </Button>
        )}
        <Button
          style={{ alignSelf: 'unset' }}
          onClick={(e) => {
            e.preventDefault();
            // setPaymentModalOpen(true);
            onSubmit();
          }}
          size={ButtonSize.Large}
          disabled={!isValid}>
          Appliquer
        </Button>
      </form>
    </>
  );
};
